import React, { useState, useRef, useEffect } from 'react';
import AppShell from '../../components/layout/AppShell';
import { shipmentApi } from '../../services/api';
import { StatusBadge, SERVICE_LABELS, addrStr } from '../../utils/helpers';
import toast from 'react-hot-toast';
import { Search, CheckCircle, XCircle } from 'lucide-react';

const SCAN_TYPES = [
  { value: 'INWARD',   label: '📥 Inward Scan',   color: 'var(--info)',    nextStatus: 'AT_HUB' },
  { value: 'OUTWARD',  label: '📤 Outward Scan',   color: 'var(--navy)',   nextStatus: 'IN_TRANSIT' },
  { value: 'DELIVERY', label: '🚚 Delivery',       color: 'var(--success)', nextStatus: 'OUT_FOR_DELIVERY' },
  { value: 'PICKUP',   label: '📦 Pickup',         color: 'var(--amber)',  nextStatus: 'PICKED_UP' },
  { value: 'DAMAGE',   label: '⚠️ Damage',          color: 'var(--danger)', nextStatus: null },
];

const DELIVERY_RESULTS = [
  { value: 'DELIVERED',          label: '✅ Delivered', status: 'DELIVERED' },
  { value: 'CUSTOMER_UNAVAILABLE',label: '🚫 Customer Unavailable', status: 'DELIVERY_ATTEMPTED' },
  { value: 'ADDRESS_INCORRECT',  label: '📍 Address Incorrect', status: 'DELIVERY_ATTEMPTED' },
  { value: 'CUSTOMER_REFUSED',   label: '❌ Customer Refused', status: 'DELIVERY_ATTEMPTED' },
  { value: 'BUSINESS_CLOSED',    label: '🏢 Business Closed', status: 'DELIVERY_ATTEMPTED' },
  { value: 'ACCESS_DENIED',      label: '🔒 Access Denied', status: 'DELIVERY_ATTEMPTED' },
];

export default function ScanPage() {
  const inputRef = useRef(null);
  const [scanType, setScanType] = useState('INWARD');
  const [query, setQuery] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showDelivery, setShowDelivery] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState('');
  const [sessionCount, setSessionCount] = useState(0);

  // Auto-focus scan input
  useEffect(() => { inputRef.current?.focus(); }, [scanType]);

  const doScan = async (trackingId) => {
    const tid = (trackingId || query).trim().toUpperCase();
    if (!tid) return;
    setScanning(true);
    setResult(null);

    try {
      // First fetch shipment detail
      const detail = await shipmentApi.getDetail(tid);
      if (!detail.success) throw new Error('Shipment not found');

      const shipment = detail.data.shipment;

      // For delivery scan, show delivery result dialog
      if (scanType === 'DELIVERY') {
        setResult({ shipment, mode: 'delivery' });
        setShowDelivery(true);
        setScanning(false);
        return;
      }

      // Determine next status
      const scanDef = SCAN_TYPES.find(s => s.value === scanType);
      const nextStatus = scanDef?.nextStatus;

      if (nextStatus) {
        await shipmentApi.updateStatus(tid, {
          status: nextStatus,
          notes: `Scan: ${scanType}`,
        });
      }

      const scanRecord = {
        trackingId: tid,
        scanType,
        status: nextStatus || shipment.status,
        customerName: shipment.customer_name,
        service: shipment.service_type,
        weight: shipment.weight_actual,
        city: addrStr(shipment.delivery_address),
        time: new Date().toISOString(),
        success: true,
      };

      setResult({ shipment, scanRecord });
      setHistory(h => [scanRecord, ...h.slice(0, 29)]);
      setSessionCount(c => c + 1);
      setQuery('');
      toast.success(`Scanned: ${tid}`);

      // Re-focus input for next scan
      setTimeout(() => inputRef.current?.focus(), 100);

    } catch (e) {
      const failRecord = {
        trackingId: tid, scanType, status: 'ERROR',
        error: e.message || 'Not found', time: new Date().toISOString(), success: false,
      };
      setResult({ error: e.message, trackingId: tid });
      setHistory(h => [failRecord, ...h.slice(0, 29)]);
      toast.error(e.message || 'Scan failed');
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 100);
    } finally {
      setScanning(false);
    }
  };

  const confirmDelivery = async (deliveryResult) => {
    const tid = result.shipment.tracking_id;
    try {
      await shipmentApi.updateStatus(tid, {
        status: deliveryResult.status,
        notes: `${deliveryResult.value}${deliveryNote ? ': ' + deliveryNote : ''}`,
      });

      const scanRecord = {
        trackingId: tid, scanType: 'DELIVERY',
        status: deliveryResult.status,
        customerName: result.shipment.customer_name,
        service: result.shipment.service_type,
        time: new Date().toISOString(), success: true,
      };
      setHistory(h => [scanRecord, ...h.slice(0, 29)]);
      setSessionCount(c => c + 1);
      setShowDelivery(false);
      setResult(null);
      setDeliveryNote('');
      setQuery('');
      toast.success(deliveryResult.status === 'DELIVERED' ? '✅ Delivered!' : '⚠️ Attempt recorded');
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
  };

  return (
    <AppShell title="Scan Terminal">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>
        {/* LEFT: Scan area */}
        <div style={{ display: 'grid', gap: 16 }}>
          {/* Scan type selector */}
          <div className="card">
            <div className="card-body" style={{ padding: 12 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {SCAN_TYPES.map(s => (
                  <button key={s.value}
                    className={`btn ${scanType === s.value ? 'btn-primary' : 'btn-outline'}`}
                    style={{ fontWeight: scanType === s.value ? 700 : 400 }}
                    onClick={() => { setScanType(s.value); setResult(null); }}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main scan input */}
          <div className="card">
            <div className="card-body" style={{ padding: 24 }}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 48, marginBottom: 8 }}>🔍</div>
                <h3 style={{ color: 'var(--navy)', marginBottom: 4 }}>
                  {SCAN_TYPES.find(s => s.value === scanType)?.label}
                </h3>
                <p style={{ color: 'var(--gray-500)', fontSize: '.85rem' }}>
                  Scan barcode or enter tracking ID manually
                </p>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  ref={inputRef}
                  className="form-control"
                  style={{ fontSize: '1.1rem', padding: '12px 16px', fontFamily: 'monospace', textTransform: 'uppercase' }}
                  placeholder="ECL PK 2026 ..."
                  value={query}
                  onChange={e => setQuery(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && doScan()}
                  autoComplete="off"
                />
                <button className="btn btn-primary btn-lg" onClick={() => doScan()} disabled={scanning || !query.trim()}>
                  {scanning ? '…' : <Search size={20} />}
                </button>
              </div>

              <p style={{ textAlign: 'center', fontSize: '.78rem', color: 'var(--gray-400)', marginTop: 10 }}>
                Press Enter or scan barcode to submit
              </p>

              {/* Session counter */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 16,
                padding: 12, background: 'var(--gray-50)', borderRadius: 8 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--navy)' }}>{sessionCount}</div>
                  <div style={{ fontSize: '.7rem', color: 'var(--gray-400)', textTransform: 'uppercase' }}>Scanned</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--success)' }}>
                    {history.filter(h => h.success).length}
                  </div>
                  <div style={{ fontSize: '.7rem', color: 'var(--gray-400)', textTransform: 'uppercase' }}>Success</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--danger)' }}>
                    {history.filter(h => !h.success).length}
                  </div>
                  <div style={{ fontSize: '.7rem', color: 'var(--gray-400)', textTransform: 'uppercase' }}>Errors</div>
                </div>
              </div>
            </div>
          </div>

          {/* Last scan result */}
          {result && !result.mode && (
            <div className="card" style={{ borderLeft: `4px solid ${result.error ? 'var(--danger)' : 'var(--success)'}` }}>
              <div className="card-body">
                {result.error ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <XCircle size={32} color="var(--danger)" />
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--danger)' }}>Scan Failed</div>
                      <div style={{ fontSize: '.85rem', fontFamily: 'monospace' }}>{result.trackingId}</div>
                      <div style={{ fontSize: '.8rem', color: 'var(--gray-500)' }}>{result.error}</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <CheckCircle size={32} color="var(--success)" style={{ flexShrink: 0, marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: 'var(--success)', marginBottom: 4 }}>Scanned Successfully</div>
                      <div style={{ fontFamily: 'monospace', fontSize: '1rem', color: 'var(--navy)', marginBottom: 8 }}>
                        {result.shipment?.tracking_id}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <ScanDetail label="Customer" v={result.shipment?.customer_name} />
                        <ScanDetail label="Status" v={<StatusBadge status={result.scanRecord?.status} />} />
                        <ScanDetail label="Service" v={SERVICE_LABELS[result.shipment?.service_type]} />
                        <ScanDetail label="Weight" v={`${result.shipment?.weight_actual} kg`} />
                        <ScanDetail label="Destination" v={addrStr(result.shipment?.delivery_address)} />
                        {result.shipment?.cod_amount > 0 &&
                          <ScanDetail label="COD" v={`PKR ${result.shipment.cod_amount}`} />}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Scan history */}
        <div className="card" style={{ position: 'sticky', top: 80 }}>
          <div className="card-header">
            <span className="card-title">Session History</span>
            {history.length > 0 && (
              <button className="btn btn-outline btn-sm" onClick={() => { setHistory([]); setSessionCount(0); }}>
                Clear
              </button>
            )}
          </div>
          <div style={{ maxHeight: 520, overflowY: 'auto' }}>
            {history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--gray-400)' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                <div style={{ fontSize: '.85rem' }}>No scans yet</div>
              </div>
            ) : (
              history.map((h, i) => (
                <div key={i} style={{
                  padding: '10px 16px',
                  borderBottom: '1px solid var(--gray-100)',
                  background: h.success ? 'transparent' : '#FEF2F2',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <code style={{ fontSize: '.75rem', color: 'var(--navy)' }}>{h.trackingId}</code>
                    {h.success
                      ? <CheckCircle size={14} color="var(--success)" />
                      : <XCircle size={14} color="var(--danger)" />}
                  </div>
                  <div style={{ fontSize: '.72rem', color: 'var(--gray-400)', marginTop: 2 }}>
                    {h.scanType} · {new Date(h.time).toLocaleTimeString()}
                  </div>
                  {h.error && <div style={{ fontSize: '.72rem', color: 'var(--danger)' }}>{h.error}</div>}
                  {h.customerName && <div style={{ fontSize: '.72rem', color: 'var(--gray-500)' }}>{h.customerName}</div>}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Delivery result modal */}
      {showDelivery && result?.shipment && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Delivery Result — {result.shipment.tracking_id}</span>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 16, padding: 12, background: 'var(--gray-50)', borderRadius: 8 }}>
                <div style={{ fontWeight: 600 }}>{result.shipment.customer_name}</div>
                <div style={{ fontSize: '.85rem', color: 'var(--gray-500)' }}>{addrStr(result.shipment.delivery_address)}</div>
                {result.shipment.cod_amount > 0 && (
                  <div style={{ fontSize: '.85rem', marginTop: 6, color: 'var(--amber)', fontWeight: 600 }}>
                    COD: PKR {result.shipment.cod_amount}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
                {DELIVERY_RESULTS.map(dr => (
                  <button key={dr.value} className="btn btn-outline w-full"
                    style={{ justifyContent: 'flex-start', padding: '10px 14px' }}
                    onClick={() => confirmDelivery(dr)}>
                    {dr.label}
                  </button>
                ))}
              </div>

              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <input className="form-control" placeholder="Additional details..."
                  value={deliveryNote} onChange={e => setDeliveryNote(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setShowDelivery(false); setResult(null); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function ScanDetail({ label, v }) {
  return (
    <div>
      <div style={{ fontSize: '.68rem', color: 'var(--gray-400)', textTransform: 'uppercase', marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: '.82rem', fontWeight: 500 }}>{v || '—'}</div>
    </div>
  );
}
